'use client';

import { useTranslations } from 'next-intl';
import { BlocksRenderer, PageBlocksRenderer } from './blocks';
import type { PageBlock } from '@impact/types';

// =============================================================================
// TYPES
// =============================================================================

export interface DynamicPageData {
  id: string;
  title: string;
  slug: string;
  content: string | null; // Legacy HTML content
  blocks: PageBlock[] | null; // Block-based content (API returns this, stored in DB as `sections`)
  template: 'content' | 'event-admin';
  featuredImageUrl: string | null;
  seo: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: string;
  } | null;
  isPublished: boolean;
}

// Helper to check if page uses blocks
function hasBlocks(page: DynamicPageData): boolean {
  return Array.isArray(page.blocks) && page.blocks.length > 0;
}

interface DynamicPageProps {
  page: DynamicPageData;
  tenantName: string;
}

// =============================================================================
// SHARED HERO SECTION COMPONENT
// =============================================================================
// Reusable hero section with featured image for all templates

interface HeroSectionProps {
  title: string;
  subtitle?: string;
  imageUrl: string;
  height?: 'sm' | 'md' | 'lg';
  overlay?: 'light' | 'dark' | 'gradient';
}

function HeroSection({
  title,
  subtitle,
  imageUrl,
  height = 'lg',
  overlay = 'gradient',
}: HeroSectionProps) {
  // Mobile-first: smaller on mobile, larger on desktop
  const heightClasses = {
    sm: 'h-[25vh] min-h-[200px] md:h-[30vh] md:min-h-[250px]',
    md: 'h-[30vh] min-h-[220px] md:h-[45vh] md:min-h-[350px]',
    lg: 'h-[35vh] min-h-[280px] md:h-[55vh] md:min-h-[450px]',
  };

  const overlayClasses = {
    light: 'bg-black/30',
    dark: 'bg-black/60',
    gradient: 'bg-gradient-to-t from-black/80 via-black/40 to-black/20',
  };

  return (
    <section className={`relative ${heightClasses[height]} bg-zinc-900 overflow-hidden`}>
      {/* Background Image */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={imageUrl} alt={title} className="absolute inset-0 h-full w-full object-cover" />

      {/* Overlay */}
      <div className={`absolute inset-0 ${overlayClasses[overlay]}`} />

      {/* Decorative Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-end">
        <div className="container pb-10 md:pb-14">
          <div className="max-w-4xl">
            <h1 className="text-2xl md:text-3xl lg:text-5xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl drop-shadow">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// SIMPLE HEADER SECTION (NO IMAGE)
// =============================================================================
// Fallback header when no featured image is provided

interface SimpleHeaderProps {
  title: string;
  subtitle?: string;
  centered?: boolean;
}

function SimpleHeader({ title, subtitle, centered = false }: SimpleHeaderProps) {
  return (
    <section className="py-16 md:py-20 bg-muted/30 border-b border-border/50">
      <div className="container">
        <div className={`max-w-4xl ${centered ? 'mx-auto text-center' : ''}`}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">{title}</h1>
          {subtitle && <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{subtitle}</p>}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// CONTENT PAGE TEMPLATE
// =============================================================================
// Standard content page with optional hero image and rich text content

function ContentTemplate({ page }: { page: DynamicPageData }) {
  return (
    <>
      {/* Hero or Simple Header */}
      {page.featuredImageUrl ? (
        <HeroSection
          title={page.title}
          imageUrl={page.featuredImageUrl}
          height="md"
          overlay="gradient"
        />
      ) : (
        <SimpleHeader title={page.title} />
      )}

      {/* Content Section */}
      {hasBlocks(page) ? (
        <PageBlocksRenderer blocks={page.blocks!} />
      ) : page.content ? (
        <section className="py-12 md:py-20">
          <div className="container">
            <article className="max-w-4xl mx-auto">
              <div
                className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-ul:text-muted-foreground prose-ol:text-muted-foreground prose-li:marker:text-primary [&>p]:mb-4 [&>h1]:mt-8 [&>h1]:mb-4 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:mt-6 [&>h3]:mb-3 [&>h4]:mt-6 [&>h4]:mb-3 [&>ul]:my-4 [&>ol]:my-4 [&_li]:mb-2"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </article>
          </div>
        </section>
      ) : null}
    </>
  );
}

// =============================================================================
// FAQ PAGE TEMPLATE
// =============================================================================
// FAQ-style page with accordion-friendly content structure

function FAQTemplate({ page }: { page: DynamicPageData }) {
  const t = useTranslations('pages');
  return (
    <>
      {/* Hero or Simple Header */}
      {page.featuredImageUrl ? (
        <HeroSection
          title={page.title}
          subtitle={t('faqDefaultSubtitle')}
          imageUrl={page.featuredImageUrl}
          height="md"
          overlay="gradient"
        />
      ) : (
        <SimpleHeader title={page.title} subtitle={t('faqDefaultSubtitle')} centered />
      )}

      {/* FAQ Content */}
      {hasBlocks(page) ? (
        <PageBlocksRenderer blocks={page.blocks!} />
      ) : page.content ? (
        <section className="py-12 md:py-20">
          <div className="container">
            <article className="max-w-3xl mx-auto">
              <div
                className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-headings:border-b prose-headings:pb-3 prose-p:text-muted-foreground prose-a:text-primary [&>p]:mb-4 [&>h1]:mt-8 [&>h1]:mb-4 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:my-4 [&>ol]:my-4 [&_li]:mb-2"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </article>
          </div>
        </section>
      ) : null}
    </>
  );
}

// =============================================================================
// CONTACT PAGE TEMPLATE
// =============================================================================
// Contact page with content and placeholder for future form

function ContactTemplate({ page }: { page: DynamicPageData }) {
  const t = useTranslations('pages');
  const tCommon = useTranslations('common');
  return (
    <>
      {/* Hero or Simple Header */}
      {page.featuredImageUrl ? (
        <HeroSection
          title={page.title}
          subtitle={t('contactDefaultTitle')}
          imageUrl={page.featuredImageUrl}
          height="md"
          overlay="gradient"
        />
      ) : (
        <SimpleHeader title={page.title} subtitle={t('contactDefaultTitle')} centered />
      )}

      {/* Content Section */}
      {hasBlocks(page) ? (
        <PageBlocksRenderer blocks={page.blocks!} />
      ) : (
        <section className="py-12 md:py-20">
          <div className="container">
            <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {/* Contact Information */}
              <article>
                {page.content && (
                  <div
                    className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-ul:text-muted-foreground [&>p]:mb-4 [&>h1]:mt-8 [&>h1]:mb-4 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:my-4 [&_li]:mb-2"
                    dangerouslySetInnerHTML={{ __html: page.content }}
                  />
                )}
              </article>

              {/* Contact Form Placeholder (legacy) */}
              <div className="bg-muted/30 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-foreground mb-6">{t('contactFormTitle')}</h2>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {tCommon('name')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      placeholder={tCommon('namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {tCommon('email')}
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      placeholder={tCommon('emailPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {tCommon('message')}
                    </label>
                    <textarea
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                      placeholder={t('contactMessagePlaceholder')}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t('sendMessage')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

// =============================================================================
// GALLERY PAGE TEMPLATE
// =============================================================================
// Gallery-style page for image-heavy content

function GalleryTemplate({ page }: { page: DynamicPageData }) {
  return (
    <>
      {/* Hero or Simple Header */}
      {page.featuredImageUrl ? (
        <HeroSection
          title={page.title}
          imageUrl={page.featuredImageUrl}
          height="lg"
          overlay="gradient"
        />
      ) : (
        <SimpleHeader title={page.title} centered />
      )}

      {/* Gallery Content */}
      {hasBlocks(page) ? (
        <PageBlocksRenderer blocks={page.blocks!} />
      ) : page.content ? (
        <section className="py-12 md:py-20">
          <div className="container">
            <article className="max-w-5xl mx-auto">
              <div
                className="prose prose-lg max-w-none prose-img:rounded-xl prose-img:shadow-lg prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground [&>p]:mb-4 [&>h1]:mt-8 [&>h1]:mb-4 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:mt-6 [&>h3]:mb-3"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </article>
          </div>
        </section>
      ) : null}
    </>
  );
}

// =============================================================================
// BLANK/LANDING PAGE TEMPLATE
// =============================================================================
// Simple wrapper that just renders the content

function BlankTemplate({ page }: { page: DynamicPageData }) {
  return (
    <>
      {/* Hero or Simple Header */}
      {page.featuredImageUrl ? (
        <HeroSection
          title={page.title}
          imageUrl={page.featuredImageUrl}
          height="md"
          overlay="gradient"
        />
      ) : (
        <SimpleHeader title={page.title} />
      )}

      {/* Content */}
      {hasBlocks(page) ? (
        <PageBlocksRenderer blocks={page.blocks!} />
      ) : page.content ? (
        <section className="py-12 md:py-20">
          <div className="container">
            <article className="max-w-4xl mx-auto">
              <div
                className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary [&>p]:mb-4 [&>h1]:mt-8 [&>h1]:mb-4 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:my-4 [&>ol]:my-4 [&_li]:mb-2"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </article>
          </div>
        </section>
      ) : null}
    </>
  );
}

// =============================================================================
// TEMPLATE RENDERER
// =============================================================================
// Selects and renders the appropriate template based on page.template

function TemplateRenderer({ page }: { page: DynamicPageData }) {
  switch (page.template) {
    case 'content':
      return <ContentTemplate page={page} />;
    case 'event-admin':
    default:
      return <ContentTemplate page={page} />;
  }
}

// =============================================================================
// MAIN DYNAMIC PAGE COMPONENT
// =============================================================================
// Wraps template with header/footer and handles layout

export function DynamicPage({ page, tenantName }: DynamicPageProps) {
  return <TemplateRenderer page={page} />;
}
